/**
 * CREATIVE DETONATOR - DASHBOARD CORE
 * Shared functionality across all dashboard pages
 *
 * Features:
 * - Authentication check (inline login if not authenticated)
 * - Data management (Ideas, Sessions, Brands, Gallery, Videos)
 * - Modal system
 * - Keyboard shortcuts
 * - Utilities
 *
 * FRONTEND-25: Uses Logger for environment-aware logging
 *
 * @version 2.0 - Refactored: Extracted Sounds, Sidebar, Toast, Storage, UserData
 */

// Import auth modules
import { authState } from '../../auth/js/state.js';
import { initDashboardAuth } from '../../auth/js/auth-init.js';
import { api } from '../../../js/api/client.js';

// Import data mapper for centralized field normalization
// v2: Fixed dbSessionToApp to include all idea fields (issue #72)
import {
  dbToAppBatch,
  dbSessionToApp,
  dbImageToApp,
  dbVideoToApp
} from '../../../js/core/data-mapper.js';

// Import Events for scoped event listener cleanup (FRONTEND-23)
import { onDocument, cleanupScope } from '../../../js/core/events.js';
// Import escapeHtml for XSS prevention in modals
import { escapeHtml } from '../../../js/core/sanitize.js';

// FRONTEND-25: Import Logger for environment-aware logging
import { createLogger } from '../../../js/core/logger.js';

// Phase 2: Import Store for centralized state management
// CRITICAL: Must be imported before any module that uses GeneratorState
import '../../../js/core/store.js';

// Import extracted modules
import { Sounds } from '../../../js/shared/sounds.js';
import { Sidebar, updateSidebarActiveState } from './sidebar.js';
import { Toast } from '../../../js/shared/toast.js';
import { Storage } from './storage.js';
import { UserData } from './user-data.js';
import { Haptics } from '../../../js/shared/haptics.js';

// FRONTEND-25: Namespaced logger for dashboard
const log = createLogger('dashboard');

// ============================================================================
// DASHBOARD CORE MODULE
// ============================================================================

const DashboardCore = (function() {
  'use strict';

  // ---------------------------------------------------------------------------
  // Configuration
  // ---------------------------------------------------------------------------
  const CONFIG = {
    sidebarCollapsedClass: 'collapsed',
    sidebarStorageKey: 'cd_sidebar_collapsed',
    soundsEnabled: true,
    animationsEnabled: true,
    toastDuration: 3000
  };

  // ---------------------------------------------------------------------------
  // Modal System
  // ---------------------------------------------------------------------------
  const Modal = {
    activeModals: [],

    init() {
      // Close modals on escape key (FRONTEND-23: scoped listener)
      onDocument('keydown', (e) => {
        if (e.key === 'Escape' && this.activeModals.length > 0) {
          this.close(this.activeModals[this.activeModals.length - 1]);
        }
      }, 'dashboard-modal-global');

      // Close on overlay click
      document.querySelectorAll('.modal-overlay').forEach(overlay => {
        overlay.addEventListener('click', (e) => {
          if (e.target === overlay) {
            this.close(overlay.id);
          }
        });
      });

      // Close buttons
      document.querySelectorAll('.modal-close').forEach(btn => {
        btn.addEventListener('click', () => {
          const modal = btn.closest('.modal-overlay');
          if (modal) this.close(modal.id);
        });
      });
    },

    closeAll() {
      this.activeModals.forEach(id => {
        const modal = document.getElementById(id);
        if (modal) modal.classList.remove('active');
      });
      this.activeModals = [];
      document.body.style.overflow = '';
    },

    /**
     * Show a confirmation modal
     */
    confirm(options = {}) {
      const {
        title = 'Confirm',
        message = 'Are you sure?',
        confirmText = 'Confirm',
        cancelText = 'Cancel',
        confirmClass = 'btn-primary',
        onConfirm = () => {},
        onCancel = () => {}
      } = options;

      // Create modal element if it doesn't exist
      let modalEl = document.getElementById('modal-confirm');
      if (!modalEl) {
        modalEl = document.createElement('div');
        modalEl.id = 'modal-confirm';
        modalEl.className = 'modal-overlay';
        document.body.appendChild(modalEl);
      }

      // Render modal content with Mac-style titlebar (XSS-safe)
      modalEl.innerHTML = `
        <div class="modal-container modal-sm">
          <div class="modal-titlebar">
            <div class="window-close modal-close" aria-label="Close"></div>
            <div class="window-title">\u25C6 ${escapeHtml(title.toUpperCase())}</div>
          </div>
          <div class="modal-body">
            <p class="modal-message">${escapeHtml(message)}</p>
          </div>
          <div class="modal-footer">
            <button class="btn btn-outline modal-cancel">${cancelText}</button>
            <button class="btn ${confirmClass} modal-confirm-btn">${confirmText}</button>
          </div>
        </div>
      `;

      // Show modal
      modalEl.classList.add('active');
      document.body.style.overflow = 'hidden';
      this.activeModals.push('modal-confirm');
      Sounds.play('click');

      // Create scoped event listener scope for this modal instance
      const confirmModalScope = `dashboard-confirm-${Date.now()}`;

      // Bind events
      const closeModal = () => {
        modalEl.classList.remove('active');
        this.activeModals = this.activeModals.filter(id => id !== 'modal-confirm');
        if (this.activeModals.length === 0) {
          document.body.style.overflow = '';
        }
        cleanupScope(confirmModalScope);
      };

      // Close button
      modalEl.querySelector('.modal-close').addEventListener('click', () => {
        closeModal();
        onCancel();
        Sounds.play('click');
      });

      // Cancel button
      modalEl.querySelector('.modal-cancel').addEventListener('click', () => {
        closeModal();
        onCancel();
        Sounds.play('click');
      });

      // Confirm button
      modalEl.querySelector('.modal-confirm-btn').addEventListener('click', () => {
        closeModal();
        onConfirm();
        Sounds.play('success');
      });

      // Click outside to cancel
      modalEl.addEventListener('click', (e) => {
        if (e.target === modalEl) {
          closeModal();
          onCancel();
        }
      });

      // ESC key to cancel (scoped listener with cleanup)
      onDocument('keydown', (e) => {
        if (e.key === 'Escape' && modalEl.classList.contains('active')) {
          closeModal();
          onCancel();
        }
      }, confirmModalScope);
    },

    /**
     * Open a custom modal with content
     */
    open(options = {}) {
      if (typeof options === 'string') {
        // Legacy: just modal ID
        const modal = document.getElementById(options);
        if (!modal) return;
        modal.classList.add('active');
        document.body.style.overflow = 'hidden';
        this.activeModals.push(options);
        Sounds.play('click');
        return;
      }

      // New: custom content modal
      const {
        title = '',
        content = '',
        size = 'medium',
        footer = ''
      } = options;

      let modalEl = document.getElementById('modal-custom');
      if (!modalEl) {
        modalEl = document.createElement('div');
        modalEl.id = 'modal-custom';
        modalEl.className = 'modal-overlay';
        document.body.appendChild(modalEl);
      }

      const sizeClass = size === 'large' ? 'modal-lg' : size === 'small' ? 'modal-sm' : '';

      modalEl.innerHTML = `
        <div class="modal-container ${sizeClass}">
          <div class="modal-header">
            <h3 class="modal-title">${title}</h3>
            <button class="modal-close" data-modal-close aria-label="Close">\u00D7</button>
          </div>
          <div class="modal-body">
            ${content}
          </div>
          ${footer ? `<div class="modal-footer">${footer}</div>` : ''}
        </div>
      `;

      modalEl.classList.add('active');
      document.body.style.overflow = 'hidden';
      this.activeModals.push('modal-custom');
      Sounds.play('click');

      // Bind close events
      modalEl.querySelector('.modal-close').addEventListener('click', () => this.close('modal-custom'));
      modalEl.querySelectorAll('[data-modal-close]').forEach(btn => {
        btn.addEventListener('click', () => this.close('modal-custom'));
      });
      modalEl.addEventListener('click', (e) => {
        if (e.target === modalEl) this.close('modal-custom');
      });
    },

    close(modalId = 'modal-custom') {
      const modal = document.getElementById(modalId);
      if (!modal) return;

      modal.classList.remove('active');
      this.activeModals = this.activeModals.filter(id => id !== modalId);

      if (this.activeModals.length === 0) {
        document.body.style.overflow = '';
      }
      Sounds.play('click');
    }
  };

  // ---------------------------------------------------------------------------
  // Ideas Management (API-backed with localStorage fallback)
  // ---------------------------------------------------------------------------
  const Ideas = {
    items: [],
    total: 0, // True count from database (not limited by pagination)
    loading: false,
    loaded: false,

    async load(forceRefresh = false) {
      if (this.loading) return this.items;
      if (this.loaded && !forceRefresh) return this.items;

      this.loading = true;

      try {
        // Increased limit to ensure client-side filtering works for larger libraries
        const response = await api.getIdeas({ limit: 1000 });
        if (response.success) {
          // Use centralized data mapper for normalization
          this.items = dbToAppBatch(response.ideas || []);
          // Capture the true total from database (not limited by pagination)
          this.total = response.total || this.items.length;
          // Cache locally for offline access
          Storage.set('ideas', this.items);
          Storage.set('ideas_total', this.total);
          log.debug(`Ideas: Loaded ${this.items.length} of ${this.total} ideas from API`);
        }
      } catch (error) {
        log.warn('Ideas: API failed, using localStorage fallback:', error.message);
        this.items = Storage.get('ideas', []);
        this.total = Storage.get('ideas_total', this.items.length);
      }

      this.loading = false;
      this.loaded = true;
      return this.items;
    },

    async save(ideaId) {
      try {
        const response = await api.saveIdea(ideaId);
        if (response.success) {
          const idea = this.items.find(i => i.id === ideaId);
          if (idea) {
            idea.saved = true;
            idea.isSaved = true;
            idea.savedAt = new Date().toISOString();
          }
          Storage.set('ideas', this.items);
        }
        return response;
      } catch (error) {
        log.error('Ideas: Save failed:', error.message);
        throw error;
      }
    },

    async unsave(ideaId) {
      try {
        const response = await api.unsaveIdea(ideaId);
        if (response.success) {
          const idea = this.items.find(i => i.id === ideaId);
          if (idea) {
            idea.saved = false;
            idea.isSaved = false;
            idea.savedAt = null;
          }
          Storage.set('ideas', this.items);
        }
        return response;
      } catch (error) {
        log.error('Ideas: Unsave failed:', error.message);
        throw error;
      }
    },

    async delete(ideaId) {
      try {
        const response = await api.deleteIdea ? await api.deleteIdea(ideaId) : { success: true };
        if (response.success) {
          this.items = this.items.filter(i => i.id !== ideaId);
          Storage.set('ideas', this.items);
        }
        return response;
      } catch (error) {
        log.error('Ideas: Delete failed:', error.message);
        throw error;
      }
    },

    getByBrand(brandId) {
      return this.items.filter(i => i.brandId === brandId);
    },

    getByTrigger(triggerCode) {
      return this.items.filter(i => i.triggerCode === triggerCode);
    },

    getByFormat(format) {
      return this.items.filter(i => i.format === format);
    },

    getSaved() {
      return this.items.filter(i => i.saved);
    },

    getById(ideaId) {
      return this.items.find(i => i.id === ideaId);
    },

    search(query) {
      const q = query.toLowerCase();
      return this.items.filter(i =>
        i.headline?.toLowerCase().includes(q) ||
        i.visual?.toLowerCase().includes(q) ||
        i.whyItWorks?.toLowerCase().includes(q)
      );
    },

    reset() {
      this.items = [];
      this.loaded = false;
    }
  };

  // ---------------------------------------------------------------------------
  // Brands Management (API-backed with localStorage fallback)
  // ---------------------------------------------------------------------------
  const Brands = {
    items: [],
    loading: false,
    loaded: false,
    loadedBrandId: null,

    async load() {
      if (this.loading) return this.items;
      if (this.loaded) return this.items;

      this.loading = true;

      try {
        const response = await api.getBrands();
        if (response.success) {
          this.items = response.brands || [];
          Storage.set('brands', this.items);
          log.debug(`Brands: Loaded ${this.items.length} brands from API`);
        }
      } catch (error) {
        log.warn('Brands: API failed, using localStorage fallback:', error.message);
        this.items = Storage.get('brands', []);
      }

      // Restore previously loaded brand
      const savedLoadedId = Storage.get('loaded_brand');
      if (savedLoadedId && this.items.find(b => b.id === savedLoadedId)) {
        this.loadedBrandId = savedLoadedId;
      }

      this.loading = false;
      this.loaded = true;
      return this.items;
    },

    async save(brand) {
      try {
        let response;
        if (brand.id && !brand.id.startsWith('brand_')) {
          response = await api.updateBrand(brand.id, brand);
        } else {
          response = await api.createBrand(brand);
        }

        if (response.success) {
          this.loaded = false;
          await this.load();
        }
        return response;
      } catch (error) {
        log.warn('Brands: API save failed, using localStorage:', error.message);
        const existing = this.items.findIndex(b => b.id === brand.id);
        if (existing >= 0) {
          this.items[existing] = { ...this.items[existing], ...brand };
        } else {
          brand.id = brand.id || `brand_${Date.now()}`;
          brand.createdAt = new Date().toISOString();
          brand.stats = { ideas: 0, images: 0, sessions: 0 };
          this.items.push(brand);
        }
        Storage.set('brands', this.items);
        return { success: true, brand };
      }
    },

    async delete(brandId) {
      try {
        const response = await api.deleteBrand(brandId);
        if (response.success) {
          this.items = this.items.filter(b => b.id !== brandId);
          Storage.set('brands', this.items);
          if (this.loadedBrandId === brandId) {
            this.eject();
          }
        }
        return response;
      } catch (error) {
        log.warn('Brands: API delete failed, using localStorage:', error.message);
        this.items = this.items.filter(b => b.id !== brandId);
        Storage.set('brands', this.items);
        if (this.loadedBrandId === brandId) {
          this.eject();
        }
        return { success: true };
      }
    },

    getById(brandId) {
      return this.items.find(b => b.id === brandId);
    },

    loadBrand(brandId) {
      const brand = this.getById(brandId);
      if (!brand) {
        log.warn('Brands: Cannot load - brand not found:', brandId);
        return null;
      }

      this.loadedBrandId = brandId;
      Storage.set('loaded_brand', brandId);

      window.dispatchEvent(new CustomEvent('brand:loaded', {
        detail: { brandId, brand }
      }));

      log.debug(`Brands: Loaded brand: ${brand.name}`);
      return brand;
    },

    eject() {
      const previousId = this.loadedBrandId;
      this.loadedBrandId = null;
      Storage.remove('loaded_brand');

      window.dispatchEvent(new CustomEvent('brand:ejected', {
        detail: { brandId: previousId }
      }));

      log.debug('Brands: Brand ejected');
    },

    getLoaded() {
      if (!this.loadedBrandId) return null;
      return this.getById(this.loadedBrandId);
    },

    isLoaded() {
      return !!this.loadedBrandId;
    },

    reset() {
      this.items = [];
      this.loaded = false;
      this.loadedBrandId = null;
    }
  };

  // ---------------------------------------------------------------------------
  // Sessions Management (API-backed with localStorage fallback)
  // ---------------------------------------------------------------------------
  const Sessions = {
    items: [],
    loading: false,
    loaded: false,

    async load(forceRefresh = false) {
      if (this.loading) return this.items;
      if (this.loaded && !forceRefresh) return this.items;

      this.loading = true;

      try {
        // Increased limit to ensure client-side filtering works
        const response = await api.getSessions({ limit: 1000 });
        if (response.success) {
          this.items = (response.sessions || []).map(dbSessionToApp);
          // Phase 2: Don't cache sessions to localStorage - too large, fetch from API
          // Storage.set('sessions', this.items);
          log.debug(`Sessions: Loaded ${this.items.length} sessions from API`);
        }
      } catch (error) {
        log.warn('Sessions: API failed, using localStorage fallback:', error.message);
        const rawSessions = Storage.get('sessions', []);
        this.items = rawSessions.map(session => {
          if (typeof session.brief === 'string') {
            session.brief = {
              product: session.brief,
              benefit: '',
              formats: session.formats || []
            };
          }
          return session;
        });
      }

      this.loading = false;
      this.loaded = true;
      return this.items;
    },

    async getById(sessionId) {
      try {
        const response = await api.getSession(sessionId);
        if (response.success) {
          return response.session;
        }
      } catch (error) {
        log.error('Sessions: Get by ID failed:', error.message);
      }
      return this.items.find(s => s.id === sessionId);
    },

    getRecent(limit = 5) {
      return this.items.slice(0, limit);
    },

    getByBrand(brandId) {
      return this.items.filter(s => s.brief?.brandId === brandId);
    },

    reset() {
      this.items = [];
      this.loaded = false;
      this.loading = false;
      Storage.remove('sessions');
      log.debug('Sessions: Reset complete - will refetch on next load');
    },

    async forceReload() {
      log.debug('Sessions: Force reloading from API...');
      this.reset();
      return this.load();
    }
  };

  // ---------------------------------------------------------------------------
  // Gallery Management (API-backed with localStorage fallback)
  // ---------------------------------------------------------------------------
  const Gallery = {
    items: [],
    total: 0, // True count from database (not limited by pagination)
    loading: false,
    loaded: false,

    async load(forceRefresh = false) {
      if (this.loading) return this.items;
      if (this.loaded && !forceRefresh) return this.items;

      this.loading = true;

      try {
        // Increased limit to ensure client-side filtering works
        const response = await api.getImages({ limit: 1000 });
        if (response.success) {
          this.items = (response.images || []).map(dbImageToApp);
          // Capture the true total from database (not limited by pagination)
          this.total = response.total || this.items.length;
          Storage.set('gallery', this.items);
          Storage.set('gallery_total', this.total);
          log.debug(`Gallery: Loaded ${this.items.length} of ${this.total} images from API`);
        }
      } catch (error) {
        log.warn('Gallery: API failed, using localStorage fallback:', error.message);
        this.items = Storage.get('gallery', []);
        this.total = Storage.get('gallery_total', this.items.length);
      }

      this.loading = false;
      this.loaded = true;
      return this.items;
    },

    getById(imageId) {
      return this.items.find(i => i.id === imageId);
    },

    getByIdea(ideaId) {
      return this.items.filter(i => i.ideaId === ideaId);
    },

    getByBrand(brandId) {
      return this.items.filter(i => i.brandId === brandId);
    },

    getByProvider(provider) {
      return this.items.filter(i => i.provider === provider);
    },

    reset() {
      this.items = [];
      this.loaded = false;
    }
  };

  // ---------------------------------------------------------------------------
  // Videos Data Store
  // ---------------------------------------------------------------------------
  const Videos = {
    items: [],
    total: 0, // True count from database (not limited by pagination)
    loading: false,
    loaded: false,

    async load(forceRefresh = false) {
      if (this.loading) return this.items;
      if (this.loaded && !forceRefresh) return this.items;

      this.loading = true;

      try {
        // Increased limit to ensure client-side filtering works
        const response = await api.getVideos({ limit: 1000 });
        if (response.success) {
          this.items = (response.videos || []).map(dbVideoToApp);
          // Capture the true total from database (not limited by pagination)
          this.total = response.total || this.items.length;
          Storage.set('videos', this.items);
          Storage.set('videos_total', this.total);
          log.debug(`Videos: Loaded ${this.items.length} of ${this.total} videos from API`);
        }
      } catch (error) {
        log.warn('Videos: API failed, using localStorage fallback:', error.message);
        this.items = Storage.get('videos', []);
        this.total = Storage.get('videos_total', this.items.length);
      }

      this.loading = false;
      this.loaded = true;
      return this.items;
    },

    getById(videoId) {
      return this.items.find(v => v.id === videoId);
    },

    getByIdea(ideaId) {
      return this.items.filter(v => v.ideaId === ideaId);
    },

    reset() {
      this.items = [];
      this.loaded = false;
    }
  };

  // ---------------------------------------------------------------------------
  // Keyboard Shortcuts
  // ---------------------------------------------------------------------------
  const Shortcuts = {
    bindings: {},

    init() {
      onDocument('keydown', (e) => {
        if (e.target.matches('input, textarea, select')) return;

        const key = [
          e.ctrlKey && 'ctrl',
          e.shiftKey && 'shift',
          e.altKey && 'alt',
          e.key.toLowerCase()
        ].filter(Boolean).join('+');

        const handler = this.bindings[key];
        if (handler) {
          e.preventDefault();
          handler();
        }
      }, 'dashboard-shortcuts');

      // Default shortcuts
      this.bind('n', () => {
        window.location.href = window.location.pathname.includes('/dashboard/')
          ? '../../generator/index.html'
          : 'features/generator/index.html';
      });

      this.bind('/', () => {
        const searchInput = document.querySelector('.search-input');
        if (searchInput) searchInput.focus();
      });
    },

    bind(key, callback) {
      this.bindings[key.toLowerCase()] = callback;
    },

    unbind(key) {
      delete this.bindings[key.toLowerCase()];
    }
  };

  // ---------------------------------------------------------------------------
  // Utilities
  // ---------------------------------------------------------------------------
  const Utils = {
    formatDate(dateString, format = 'short') {
      const date = new Date(dateString);
      const now = new Date();
      const diff = now - date;

      if (diff < 60000) return 'Just now';
      if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
      if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
      if (diff < 172800000) return 'Yesterday';

      const options = format === 'long'
        ? { year: 'numeric', month: 'long', day: 'numeric' }
        : { month: 'short', day: 'numeric' };

      return date.toLocaleDateString('en-US', options);
    },

    formatNumber(num) {
      if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
      if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
      return num.toString();
    },

    truncate(str, length = 50) {
      if (str.length <= length) return str;
      return str.substring(0, length) + '...';
    },

    debounce(func, wait) {
      let timeout;
      return function executedFunction(...args) {
        clearTimeout(timeout);
        timeout = setTimeout(() => func.apply(this, args), wait);
      };
    },

    copyToClipboard(text) {
      return navigator.clipboard.writeText(text).then(() => {
        Toast.success('Copied to clipboard!');
        Sounds.play('success');
        return true;
      }).catch(() => {
        Toast.error('Failed to copy');
        return false;
      });
    },

    haptic(pattern = 12) {
      if (!navigator.vibrate) return false;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;
      if (!window.matchMedia('(max-width: 1024px)').matches) return false;
      navigator.vibrate(pattern);
      return true;
    },

    touchIdea(ideaId) {
      const idea = Ideas.items.find(i => String(i.id) === String(ideaId));
      if (idea) {
        const now = new Date().toISOString();
        idea.updatedAt = now;
        idea.updated_at = now;
        log.debug('Utils: Touched idea timestamp');
      }
    }
  };

  // ---------------------------------------------------------------------------
  // Initialize Dashboard (after auth confirmed)
  // ---------------------------------------------------------------------------
  let dashboardReady = false;
  let initializationPromise = null;

  // ---------------------------------------------------------------------------
  // Profile Widget Dropdown
  // ---------------------------------------------------------------------------
  function initProfileDropdown() {
    const widget = document.getElementById('profile-widget');
    const trigger = document.getElementById('profile-trigger');
    const logout = document.getElementById('profile-logout');

    if (!widget || !trigger) return;

    trigger.addEventListener('click', (e) => {
      e.stopPropagation();
      widget.classList.toggle('open');
      Sounds.play('click');
    });

    onDocument('click', (e) => {
      if (!widget.contains(e.target)) {
        widget.classList.remove('open');
      }
    }, 'dashboard-profile-dropdown');

    onDocument('keydown', (e) => {
      if (e.key === 'Escape') {
        widget.classList.remove('open');
      }
    }, 'dashboard-profile-dropdown');

    widget.querySelectorAll('.profile-item').forEach(item => {
      item.addEventListener('click', () => Sounds.play('click'));
    });

    if (logout) {
      logout.addEventListener('click', async (e) => {
        e.preventDefault();
        Sounds.play('click');

        try {
          if (window.Auth?.signOut) {
            await window.Auth.signOut();
          } else if (authState.supabase) {
            await authState.supabase.auth.signOut();
          }
          window.location.href = '../../../index.html';
        } catch (error) {
          log.error('Profile: Logout error:', error.message);
          Toast.show('Logout failed', 'error');
        }
      });
    }

    log.debug('Profile dropdown initialized');
  }

  async function initDashboard() {
    if (initializationPromise) return initializationPromise;

    initializationPromise = (async () => {
      try {
        // Load sidebar component first
        await Sidebar.loadComponent();

        // Initialize all modules
        Sounds.init();
        Sidebar.init();
        Toast.init();
        Modal.init();
        Shortcuts.init();

        // Load user data from auth state
        await UserData.loadFromAuth();

        // Initialize profile dropdown
        initProfileDropdown();

        // Set sidebar active state
        updateSidebarActiveState();

        // Load counts for sidebar badges
        await loadSidebarCounts();

        // Add click sounds to buttons
        document.querySelectorAll('button, .btn, .nav-item').forEach(el => {
          el.addEventListener('click', () => Sounds.play('click'));
        });

        // Enable haptic feedback for mobile devices
        Haptics.autoAttach();

        dashboardReady = true;
        window.dispatchEvent(new CustomEvent('dashboard:ready'));

        log.info('Dashboard core initialized');
      } catch (error) {
        log.error('Dashboard initialization error:', error.message);
        dashboardReady = true;
        window.dispatchEvent(new CustomEvent('dashboard:ready'));
      }
    })();

    return initializationPromise;
  }

  /**
   * Load real counts for sidebar badges
   * Uses total from database (not limited by pagination)
   */
  async function loadSidebarCounts(forceRefresh = false) {
    try {
      // Load data - we need the items for saved filter, but use .total for badges
      await Promise.all([
        Ideas.load(forceRefresh),
        Gallery.load(forceRefresh),
        Videos.load(forceRefresh)
      ]);

      // USE THE TOTALS FROM DB, NOT THE ARRAY LENGTH
      // This shows the true count even if we only loaded 100 items
      const ideaCount = Ideas.total;
      const mediaCount = Gallery.total + Videos.total;

      // Update Dashboard Hero Stats
      document.querySelectorAll('.stat-ideas').forEach(el => {
        el.textContent = ideaCount;
      });
      document.querySelectorAll('.stat-images').forEach(el => {
        el.textContent = mediaCount;
      });

      // Note: 'Saved' count is calculated from loaded items since we don't have a backend stat
      // This is a best-effort approximation without a dedicated API endpoint
      document.querySelectorAll('.stat-saved').forEach(el => {
        el.textContent = Ideas.items.filter(i => i.saved).length;
      });

      // Update Sidebar Badges - Use robust data-page selectors
      const ideasBadge = document.querySelector('.nav-item[data-page="ideas"] .nav-badge');
      const galleryBadge = document.querySelector('.nav-item[data-page="gallery"] .nav-badge');

      if (ideasBadge) {
        ideasBadge.textContent = ideaCount;
        ideasBadge.style.display = ideaCount > 0 ? 'inline-block' : 'none';
      }
      if (galleryBadge) {
        galleryBadge.textContent = mediaCount;
        galleryBadge.style.display = mediaCount > 0 ? 'inline-block' : 'none';
      }

      log.debug(`Sidebar counts: ${ideaCount} ideas, ${mediaCount} media`);
    } catch (error) {
      log.warn('Dashboard: Failed to load sidebar counts:', error.message);
    }
  }

  function isDashboardReady() {
    return dashboardReady;
  }

  async function waitForDashboard(timeout = 10000) {
    if (dashboardReady) return true;

    return new Promise((resolve) => {
      const timeoutId = setTimeout(() => {
        log.warn('Dashboard: Timeout waiting for dashboard:ready');
        resolve(false);
      }, timeout);

      const handler = () => {
        clearTimeout(timeoutId);
        resolve(true);
      };

      window.addEventListener('dashboard:ready', handler, { once: true });
    });
  }

  function reinitialize() {
    Ideas.reset();
    Gallery.reset();
    Sessions.reset();
    Brands.reset();
    updateSidebarActiveState();
    loadSidebarCounts();
  }

  // ---------------------------------------------------------------------------
  // Main Init with Auth Check
  // ---------------------------------------------------------------------------
  async function init() {
    const authScreen = document.getElementById('dashboard-auth-screen');
    const dashboardApp = document.getElementById('dashboard-app');

    const showDashboard = () => {
      if (authScreen) authScreen.classList.add('hidden');
      if (dashboardApp) dashboardApp.classList.remove('hidden');
    };

    const showLogin = () => {
      if (dashboardApp) dashboardApp.classList.add('hidden');
      if (authScreen) authScreen.classList.remove('hidden');
    };

    log.debug('Dashboard: Initializing...');

    await initDashboardAuth(
      async () => {
        log.debug('Dashboard: Authentication confirmed. Rendering app.');
        showDashboard();
        await initDashboard();
      },
      showLogin,
      showDashboard
    );
  }

  // Auto-init on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------
  return {
    CONFIG,
    Sounds,
    Sidebar,
    Toast,
    Modal,
    Storage,
    UserData,
    Ideas,
    Brands,
    Sessions,
    Gallery,
    Videos,
    Shortcuts,
    Utils,
    init,
    isDashboardReady,
    waitForDashboard,
    reinitialize,
    updateSidebarActiveState,
    loadSidebarCounts
  };
})();

// Export for ES6 modules
if (typeof module !== 'undefined' && module.exports) {
  module.exports = DashboardCore;
}

// Global reference
window.DashboardCore = DashboardCore;
